import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, Clock, MapPin, Hourglass, Video, UserRound } from "lucide-react";
import { CALENDARIO_FALLBACK, scrollToId } from "@/lib/site";
import { Stamp } from "@/components/decor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DateRow = ({ item, idx }) => (
  <motion.li
    data-testid={`data-${item.anno}-${idx + 1}`}
    initial={{ opacity: 0, x: -24 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ duration: 0.5, delay: idx * 0.07 }}
    className="flex items-center gap-4 border-b border-dashed border-[#E2D9CD] py-4 last:border-b-0 sm:gap-6"
  >
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0D9488] font-display text-lg font-semibold text-white">
      {((item.numero - 1) % 6) + 1}
    </span>
    <span className="flex-1 text-sm font-semibold text-[#0C1B33] sm:text-base">{item.titolo}</span>
    <span className="hidden flex-1 border-b border-dotted border-[#0C1B33]/20 sm:block" aria-hidden />
    <span className="text-right">
      <span className="flex items-center justify-end gap-1.5 text-sm font-semibold text-[#0C1B33]">
        <CalendarDays className="h-4 w-4 text-[#0D9488]" />
        {item.data}
      </span>
      <span className="mt-0.5 flex items-center justify-end gap-1.5 text-xs text-[#718096]">
        <Clock className="h-3.5 w-3.5" />
        {item.ora}
      </span>
    </span>
  </motion.li>
);

const Calendario = ({ posti }) => {
  const [items, setItems] = useState(CALENDARIO_FALLBACK);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/calendario`)
      .then(({ data }) => {
        if (alive && data.items?.length) setItems(data.items);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const presenzaItems = items.filter((i) => i.modalita !== "online");
  const per2026 = presenzaItems.filter((i) => i.anno === 2026).sort((a, b) => a.numero - b.numero);
  const per2027 = presenzaItems.filter((i) => i.anno === 2027).sort((a, b) => a.numero - b.numero);
  const onlineC1 = items.filter((i) => i.modalita === "online" && i.numero <= 6).sort((a, b) => a.numero - b.numero);
  const onlineC2 = items.filter((i) => i.modalita === "online" && i.numero > 6).sort((a, b) => a.numero - b.numero);

  return (
    <section id="calendario" className="relative bg-[#F4EFEA] py-20 sm:py-28" data-testid="calendario-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-[#0D9488]">04 — Le date</p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-[#0C1B33] sm:text-5xl">
            Arcipelago di gruppo in presenza
          </h2>
          <p className="mt-4 flex items-start gap-2 text-base leading-relaxed text-[#475569] sm:text-lg">
            <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#0D9488]" />
            Via Generale Guisan 8B, 6900 Lugano Paradiso — gruppo aperto, un mercoledì al mese.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className={`relative rounded-2xl border border-[#E2D9CD] bg-[#FFFDF9] p-6 shadow-[0_14px_40px_rgba(12,27,51,0.07)] sm:p-9 ${per2027.length > 0 ? "lg:col-span-12" : "lg:col-span-7"}`}
          >
            {posti?.presenza_esaurita && (
              <span
                data-testid="calendario-2026-completo"
                className="absolute -right-2 -top-4 rotate-6 rounded-full border-[3px] border-[#C93B2B] bg-[#FFFDF9] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-[#C93B2B] shadow-md"
              >
                Completo
              </span>
            )}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-display text-2xl font-semibold text-[#0C1B33]">Calendario 2026</h3>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#0D9488]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#0D9488]">
                  {per2026.length} incontri · gruppo aperto
                </span>
                {posti?.presenza_esaurita && (
                  <span
                    data-testid="posti-rimanenti-calendario"
                    className="rounded-full bg-[#C93B2B]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#C93B2B]"
                  >
                    Posti esauriti
                  </span>
                )}
              </div>
            </div>
            <ul>
              {per2026.map((item, idx) => (
                <DateRow key={item.id} item={item} idx={idx} />
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className={`relative flex flex-col rounded-2xl p-8 sm:p-10 ${
              per2027.length > 0
                ? "border border-[#E2D9CD] bg-[#FFFDF9] shadow-[0_14px_40px_rgba(12,27,51,0.07)] lg:col-span-12"
                : "border-2 border-dashed border-[#0C1B33]/25 bg-[#FFFDF9]/60 lg:col-span-5"
            }`}
            data-testid="calendario-2027-card"
          >
            {per2027.length === 0 && (
              <Stamp small testId="calendario-2027-stamp" className="absolute -right-4 -top-6 h-24 w-24 bg-[#F4EFEA]/80" />
            )}

            {per2027.length > 0 ? (
              <>
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-display text-2xl font-semibold text-[#0C1B33]">Calendario 2027</h3>
                    <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#0D9488]">
                      Due cicli da 6 incontri · un mercoledì al mese, 17:00 – 18:30
                    </p>
                  </div>
                  <span className="rounded-full bg-[#0D9488]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#0D9488]">
                    Iscrizioni aperte
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-10 md:grid-cols-2" data-testid="calendario-2027-list">
                  {[
                    { label: "Primo ciclo · Gennaio – Giugno", key: "presenza_1", rows: per2027.filter((i) => i.numero <= 6), testId: "ciclo-2027-1" },
                    { label: "Secondo ciclo · Luglio – Dicembre", key: "presenza_2", rows: per2027.filter((i) => i.numero > 6), testId: "ciclo-2027-2" },
                  ].map((ciclo) =>
                    ciclo.rows.length > 0 ? (
                      <div key={ciclo.label} data-testid={ciclo.testId}>
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#C93B2B]">
                          {ciclo.label}
                          {posti?.cicli?.[ciclo.key] && (
                            <span className="ml-2 normal-case text-[#0D9488]" data-testid={`posti-${ciclo.key}`}>
                              · {posti.cicli[ciclo.key].rimasti <= 0 ? "posti esauriti" : `${posti.cicli[ciclo.key].rimasti} posti liberi su ${posti.cicli[ciclo.key].capienza}`}
                            </span>
                          )}
                        </p>
                        <ul>
                          {ciclo.rows.map((item, idx) => (
                            <DateRow key={item.id} item={item} idx={idx} />
                          ))}
                        </ul>
                      </div>
                    ) : null
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#0C1B33]/5 text-[#0C1B33]">
                  <Hourglass className="h-6 w-6" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-[#0C1B33]">Ciclo 2027</h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-[#C93B2B]">Coming soon · date in definizione</p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-[#475569]">
                  Le date dei gruppi in presenza per il 2027 sono in fase di pianificazione e verranno
                  pubblicate qui appena fissate. Nel frattempo puoi già segnalare il tuo interesse:
                  compilando il modulo di iscrizione verrai ricontattato personalmente appena il nuovo
                  calendario sarà disponibile.
                </p>
                <button
                  data-testid="calendario-2027-cta"
                  onClick={() => scrollToId("iscrizione")}
                  className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-[#0C1B33]/25 px-6 py-3 text-sm font-semibold text-[#0C1B33] transition-[background-color,color,border-color] duration-300 hover:border-[#0D9488] hover:bg-[#0D9488] hover:text-white"
                >
                  Segnala il tuo interesse
                </button>
              </>
            )}
          </motion.div>
        </div>

        {(onlineC1.length > 0 || onlineC2.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="mt-10 rounded-2xl border border-[#E2D9CD] bg-[#FFFDF9] p-8 shadow-[0_14px_40px_rgba(12,27,51,0.07)] sm:p-10"
            data-testid="calendario-online-card"
          >
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#0D9488]/10 text-[#0D9488]">
                  <Video className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-2xl font-semibold text-[#0C1B33]">
                    Psicoeducazione di gruppo online
                  </h3>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#0D9488]">
                    Giovedì · 20:00 – 22:00 · max 5 partecipanti per ciclo
                  </p>
                </div>
              </div>
              {posti?.cicli && (
                <span
                  className="rounded-full bg-[#0C1B33]/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#0C1B33]"
                  data-testid="posti-online-calendario"
                >
                  {posti.cicli.online_1.rimasti + posti.cicli.online_2.rimasti} posti liberi su 10
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
              {[
                { label: "Primo ciclo · Gennaio – Giugno", key: "online_1", rows: onlineC1, testId: "ciclo-online-1" },
                { label: "Secondo ciclo · Luglio – Dicembre", key: "online_2", rows: onlineC2, testId: "ciclo-online-2" },
              ].map((ciclo) =>
                ciclo.rows.length > 0 ? (
                  <div key={ciclo.label} data-testid={ciclo.testId}>
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#C93B2B]">
                      {ciclo.label}
                      {posti?.cicli?.[ciclo.key] && (
                        <span className="ml-2 normal-case text-[#0D9488]" data-testid={`posti-${ciclo.key}`}>
                          · {posti.cicli[ciclo.key].rimasti <= 0
                            ? "posti esauriti"
                            : `${posti.cicli[ciclo.key].rimasti} posti liberi su ${posti.cicli[ciclo.key].capienza}`}
                        </span>
                      )}
                    </p>
                    <ul>
                      {ciclo.rows.map((item, idx) => (
                        <DateRow key={item.id} item={item} idx={idx} />
                      ))}
                    </ul>
                  </div>
                ) : null
              )}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-10 flex flex-col items-start gap-5 rounded-2xl border border-[#E2D9CD] bg-[#FFFDF9] p-8 shadow-[0_14px_40px_rgba(12,27,51,0.07)] sm:flex-row sm:items-center sm:p-10"
          data-testid="calendario-individuale-card"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#0C1B33]/5 text-[#0C1B33]">
            <UserRound className="h-6 w-6" />
          </span>
          <div className="flex-1">
            <h3 className="font-display text-2xl font-semibold text-[#0C1B33]">Percorso individuale</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#475569]">
              Senza date fisse: <strong>6 incontri da 75 minuti</strong> che concordiamo insieme,
              in presenza a Lugano Paradiso oppure online. L'iscrizione è sempre aperta.
            </p>
          </div>
          <button
            data-testid="calendario-individuale-cta"
            onClick={() => scrollToId("iscrizione")}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#0C1B33]/25 px-6 py-3 text-sm font-semibold text-[#0C1B33] transition-[background-color,color,border-color] duration-300 hover:border-[#0D9488] hover:bg-[#0D9488] hover:text-white"
          >
            Iscriviti al percorso individuale
          </button>
        </motion.div>

        {per2027.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="mx-auto mt-12 grid max-w-3xl grid-cols-1 gap-8 sm:grid-cols-2"
            data-testid="locandine-2027"
          >
            {[
              { src: "/locandine/arcipelago-2027-gen-giu.jpg", alt: "Locandina Arcipelago 2027 — primo ciclo, gennaio–giugno", rot: "-rotate-2" },
              { src: "/locandine/arcipelago-2027-lug-dic.jpg", alt: "Locandina Arcipelago 2027 — secondo ciclo, luglio–dicembre", rot: "rotate-2" },
            ].map((img) => (
              <figure key={img.src} className={`${img.rot} transition-transform duration-500 hover:rotate-0 hover:scale-[1.02]`}>
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full rounded-xl border border-[#E2D9CD] shadow-[0_20px_45px_rgba(12,27,51,0.18)]"
                />
              </figure>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Calendario;
